import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import React from "react";
import { LanguageProvider } from "./contexts/LanguageContext";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyAccount from "./pages/VerifyAccount";
import ProfileCompletion from "./pages/ProfileCompletion";
import Dashboard from "./pages/Dashboard";import Assessment from "./pages/Assessment";
import Careers from "./pages/Careers";
import Courses from "./pages/Courses";
import MyCourses from "./pages/MyCourses";
import CoursePlayer from "./pages/CoursePlayer";
import AdminCoursesManagement from "./pages/AdminCoursesManagement";
import AdminCertificateDesigner from "./pages/AdminCertificateDesigner";
import TopColleges from "./pages/TopColleges";
import CourseDetailPublic from "./pages/CourseDetailPublic";
import Community from "./pages/Community";
import CommunityForum from "./pages/CommunityForum";
import CommunityMentoring from "./pages/CommunityMentoring";
import CommunitySuccessStories from "./pages/CommunitySuccessStories";
import CommunityStudyGroups from "./pages/CommunityStudyGroups";
import CommunityQA from "./pages/CommunityQA";
import CareerComparison from "./pages/CareerComparison";
import AIInterviewBuddy from "./pages/AIInterviewBuddy";
import ExamPrep from "./pages/ExamPrep";
import ExamDetail from "./pages/ExamDetail";
import PracticeSession from "./pages/PracticeSession";
import MockTestSession from "./pages/MockTestSession";
import Chatbot from "./pages/Chatbot";
import CareerTwin from "./pages/CareerTwin";
import CareerPivotPlanner from "./pages/CareerPivotPlanner";
import SkillDNA from "./pages/SkillDNA";
import Consultation from "./pages/Consultation";
import Leaderboard from "./pages/Leaderboard";
import CareerDetail from "./pages/CareerDetail";
import Scholarships from "./pages/Scholarships";
import Exams from "./pages/Exams";
import Resources from "./pages/Resources";
import Events from "./pages/Events";
import Mentors from "./pages/Mentors";
import Quizzes from "./pages/Quizzes";
import Colleges from "./pages/Colleges";
import CollegeDetail from "./pages/CollegeDetail";
import Notifications from "./pages/Notifications";
import AdminDashboard from "./pages/AdminDashboard";
import AdminPYQManagement from "./pages/AdminPYQManagement";
import AdminPanel from "./pages/AdminPanel";
import ParentDashboard from "./pages/ParentDashboard";
import DatabaseViewer from "./pages/DatabaseViewer";
import AssessmentManagement from "./pages/AssessmentManagement";
import Profile from "./pages/Profile";
import PreAssessmentForm from "./pages/PreAssessmentForm";
import Subscriptions from "./pages/Subscriptions";
import ConsultationBooking from "./pages/ConsultationBooking";
import MyConsultations from "./pages/MyConsultations";
import AdminPaymentSettings from "./pages/AdminPaymentSettings";
import AdminConsultations from "./pages/AdminConsultations";
import AdminPricingManagement from "./pages/AdminPricingManagement";
import Opportunities from "./pages/Opportunities";
import OpportunityDetail from "./pages/OpportunityDetail";
import MyApplications from "./pages/MyApplications";
import SavedOpportunities from "./pages/SavedOpportunities";
import StudyPlanner from "./pages/StudyPlanner";
import Support from "./pages/Support";
import ChatWidget from "./components/ChatWidget";
import PWAInstallBanner from "./components/PWAInstallBanner";
import Badges from "./pages/Badges";
import Rewards from "./pages/Rewards";
import AdminGamification from "./pages/AdminGamification";
import ProgressDashboard from "./pages/ProgressDashboard";
import SkillTests from "./pages/SkillTests";
import SkillTestTake from "./pages/SkillTestTake";
import SkillGapAnalysis from "./pages/SkillGapAnalysis";
import UserDetailPage from "./pages/UserDetailPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import AcceptableUsePolicy from "./pages/AcceptableUsePolicy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CookieConsentBanner from "./components/CookieConsentBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Dashboard wrapper to handle OAuth callback
function DashboardWrapper({ user, login }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    
    if (tokenFromUrl && !user) {
      setLoading(true);
      // Fetch user data with the token
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${tokenFromUrl}` }
      })
      .then(response => {
        localStorage.setItem('token', tokenFromUrl);
        localStorage.setItem('user', JSON.stringify(response.data));
        login(tokenFromUrl, response.data);
        // Clear token from URL
        navigate('/dashboard', { replace: true });
      })
      .catch(error => {
        console.error('Failed to authenticate:', error);
        navigate('/login', { replace: true });
      })
      .finally(() => setLoading(false));
    }
  }, [location, user, login, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Render appropriate dashboard based on role
  if (user.role === "parent") {
    return <ParentDashboard />;
  } else if (user.role === "admin" || user.role === "school_admin") {
    return <AdminPanel />;
  } else {
    return <Dashboard />;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    toast.success("Welcome back!");
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    toast.info("Logged out successfully");
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
        <div className="App">
          <Toaster position="top-right" richColors />
          <PWAInstallBanner />
          <BrowserRouter>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            
            {/* Authentication Pages */}
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage />} />
            <Route path="/verify-account" element={<VerifyAccount />} />
            <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/complete-profile" element={user ? <ProfileCompletion /> : <Navigate to="/login" />} />
            
            {/* Legacy Home Route (kept for backward compatibility) */}
            <Route path="/home" element={<Home />} />
            
            <Route
              path="/dashboard"
              element={
                <DashboardWrapper user={user} login={login} />
              }
            />
            <Route
              path="/assessment"
              element={user ? <Assessment /> : <Navigate to="/login" />}
            />
            <Route
              path="/careers"
              element={user ? <Careers /> : <Navigate to="/" />}
            />
            <Route
              path="/courses"
              element={user ? <Courses /> : <Navigate to="/" />}
            />
            <Route
              path="/my-courses"
              element={user ? <MyCourses /> : <Navigate to="/" />}
            />
            <Route
              path="/course-player/:courseId"
              element={user ? <CoursePlayer /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/courses-management"
              element={user?.role === 'admin' ? <AdminCoursesManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/certificate-designer"
              element={user?.role === 'admin' ? <AdminCertificateDesigner /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/exam-prep/pyq"
              element={user?.role === 'admin' ? <AdminPYQManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/top-colleges"
              element={user ? <TopColleges /> : <Navigate to="/" />}
            />
            <Route
              path="/course/:courseId"
              element={<CourseDetailPublic />}
            />
            <Route
              path="/community/forum"
              element={user ? <CommunityForum /> : <Navigate to="/" />}
            />
            <Route
              path="/community/mentoring"
              element={user ? <CommunityMentoring /> : <Navigate to="/" />}
            />
            <Route
              path="/community/success-stories"
              element={user ? <CommunitySuccessStories /> : <Navigate to="/" />}
            />
            <Route
              path="/community/study-groups"
              element={user ? <CommunityStudyGroups /> : <Navigate to="/" />}
            />
            <Route
              path="/community/qa"
              element={user ? <CommunityQA /> : <Navigate to="/" />}
            />
            <Route
              path="/community"
              element={user ? <Community /> : <Navigate to="/" />}
            />

            <Route
              path="/careers/:id"
              element={user ? <CareerDetail /> : <Navigate to="/" />}
            />
            <Route
              path="/career-comparison"
              element={user ? <CareerComparison /> : <Navigate to="/" />}
            />
            <Route
              path="/interview-buddy"
              element={user ? <AIInterviewBuddy /> : <Navigate to="/" />}
            />
            {/* Keep old route for backward compatibility */}
            <Route
              path="/mock-interview"
              element={<Navigate to="/interview-buddy" replace />}
            />
            {/* Exam Preparation (Phase 1 + 2) */}
            <Route
              path="/exam-prep"
              element={user ? <ExamPrep /> : <Navigate to="/" />}
            />
            <Route
              path="/exam-prep/:examId"
              element={user ? <ExamDetail /> : <Navigate to="/" />}
            />
            <Route
              path="/exam-prep/practice/:sessionId"
              element={user ? <PracticeSession /> : <Navigate to="/" />}
            />
            <Route
              path="/exam-prep/mock-test/:sessionId"
              element={user ? <MockTestSession /> : <Navigate to="/" />}
            />
            <Route
              path="/chatbot"
              element={user ? <Chatbot /> : <Navigate to="/" />}
            />
            <Route
              path="/career-twin"
              element={user ? <CareerTwin /> : <Navigate to="/" />}
            />
            <Route
              path="/career-pivot"
              element={user ? <CareerPivotPlanner /> : <Navigate to="/" />}
            />
            <Route
              path="/skill-dna"
              element={user ? <SkillDNA /> : <Navigate to="/" />}
            />
            <Route
              path="/consultation"
              element={user ? <Consultation /> : <Navigate to="/" />}
            />
            <Route
              path="/leaderboard"
              element={user ? <Leaderboard /> : <Navigate to="/" />}
            />
            <Route
              path="/scholarships"
              element={user ? <Scholarships /> : <Navigate to="/" />}
            />
            <Route
              path="/exams"
              element={user ? <Exams /> : <Navigate to="/" />}
            />
            <Route
              path="/resources"
              element={user ? <Resources /> : <Navigate to="/" />}
            />
            <Route
              path="/events"
              element={user ? <Events /> : <Navigate to="/" />}
            />
            <Route
              path="/mentors"
              element={user ? <Mentors /> : <Navigate to="/" />}
            />
            <Route
              path="/quizzes"
              element={user ? <Quizzes /> : <Navigate to="/" />}
            />
            <Route
              path="/colleges"
              element={user ? <Colleges /> : <Navigate to="/" />}
            />
            <Route
              path="/college/:collegeId"
              element={user ? <CollegeDetail /> : <Navigate to="/" />}
            />
            <Route
              path="/notifications"
              element={user ? <Notifications /> : <Navigate to="/" />}
            />
            <Route
              path="/admin"
              element={user ? <AdminPanel /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/users/:userId"
              element={user ? <UserDetailPage /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/old"
              element={user ? <AdminDashboard /> : <Navigate to="/" />}
            />
            <Route
              path="/parent"
              element={user ? <ParentDashboard /> : <Navigate to="/" />}
            />
            <Route
              path="/database"
              element={user ? <DatabaseViewer /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/assessments"
              element={user ? <AssessmentManagement /> : <Navigate to="/" />}
            />
            <Route
              path="/profile"
              element={user ? <Profile /> : <Navigate to="/" />}
            />
            <Route
              path="/pre-assessment"
              element={user ? <PreAssessmentForm /> : <Navigate to="/" />}
            />
            <Route
              path="/subscriptions"
              element={user ? <Subscriptions /> : <Navigate to="/" />}
            />
            <Route
              path="/consultation/book"
              element={user ? <ConsultationBooking /> : <Navigate to="/" />}
            />
            <Route
              path="/consultations"
              element={user ? <MyConsultations /> : <Navigate to="/" />}
            />
            <Route
              path="/opportunities"
              element={user ? <Opportunities /> : <Navigate to="/" />}
            />
            <Route
              path="/opportunities/:id"
              element={user ? <OpportunityDetail /> : <Navigate to="/" />}
            />
            <Route
              path="/my-applications"
              element={user ? <MyApplications /> : <Navigate to="/" />}
            />
            <Route
              path="/saved-opportunities"
              element={user ? <SavedOpportunities /> : <Navigate to="/" />}
            />
            <Route
              path="/study-planner"
              element={user ? <StudyPlanner /> : <Navigate to="/" />}
            />
            <Route
              path="/support"
              element={user ? <Support /> : <Navigate to="/" />}
            />
            <Route
              path="/progress"
              element={user ? <ProgressDashboard /> : <Navigate to="/" />}
            />
            <Route
              path="/skill-tests"
              element={user ? <SkillTests /> : <Navigate to="/" />}
            />
            <Route
              path="/skill-test/take"
              element={user ? <SkillTestTake /> : <Navigate to="/" />}
            />
            <Route
              path="/skill-test/gap-analysis"
              element={user ? <SkillGapAnalysis /> : <Navigate to="/" />}
            />
            <Route
              path="/badges"
              element={user ? <Badges /> : <Navigate to="/" />}
            />
            <Route
              path="/rewards"
              element={user ? <Rewards /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/gamification"
              element={user ? <AdminGamification /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/payment-settings"
              element={user ? <AdminPaymentSettings /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/consultations"
              element={user ? <AdminConsultations /> : <Navigate to="/" />}
            />
            <Route
              path="/admin/pricing"
              element={user ? <AdminPricingManagement /> : <Navigate to="/" />}
            />
            {/* Legal & Compliance Pages (Public) */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
          </Routes>
          {/* Floating Chat Widget - Available on all pages */}
          <ChatWidget />
          <CookieConsentBanner />
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
    </LanguageProvider>
  );
}

export default App;